/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
// src/components/items/ItemPicker.tsx
import * as React from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { ChevronsUpDown, Check, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCreateItemMutation, useItemsQuery } from '@/hooks/useItems'
import { toast } from 'sonner'

export type PickerItem = {
  id: string
  name: string
  sku?: string | null
  unitPrice?: number | null
}

export default function ItemPicker({
  value,
  onChange,
  placeholder = 'Rechercher un article…',
  autoCreateUnitPrice = 0,
}: {
  value?: PickerItem | null
  onChange: (it: PickerItem) => void
  placeholder?: string
  autoCreateUnitPrice?: number
}) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')

  // ✅ charge toute la liste une fois
  const { data: items, isLoading } = useItemsQuery()
  const products = items?.data ?? []

  const createMut = useCreateItemMutation()

  // tri simple par nom (optionnel)
  const rows = React.useMemo(
    () =>
      [...products].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'fr', {
          sensitivity: 'base',
        })
      ),
    [products]
  )

  const display = value?.name ?? 'Sélectionner…'

  const createNew = async () => {
    const name = query.trim()
    if (!name) return
    try {
      const created = await createMut.mutateAsync({
        name,
        unitPrice: autoCreateUnitPrice,
      })
      onChange({
        id: created.id,
        name: created.name,
        sku: (created as any).sku ?? null,
        unitPrice: Number((created as any).unitPrice ?? 0),
      })
      toast.success('Article créé')
      setOpen(false)
      setQuery('')
    } catch (e: any) {
      toast.error('Création échouée', {
        description: e?.response?.data?.message ?? 'Impossible de créer',
      })
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          className='w-full justify-between'
          disabled={isLoading}
        >
          <span className='truncate'>{display}</span>
          <ChevronsUpDown className='ml-2 h-4 w-4 opacity-50' />
        </Button>
      </PopoverTrigger>

      <PopoverContent className='p-0 w-[320px]'>
        <Command /* filtre local */>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={placeholder}
          />

          <CommandEmpty>
            {isLoading ? (
              'Chargement…'
            ) : (
              <div className='p-2 text-sm space-y-2'>
                <div>Aucun résultat</div>
                <Button variant='outline' size='sm' onClick={createNew}>
                  <Plus className='h-4 w-4 mr-1' /> Créer «{' '}
                  {query.trim() || '…'} »
                </Button>
              </div>
            )}
          </CommandEmpty>

          <CommandGroup>
            {rows.map((it) => (
              <CommandItem
                key={it.id}
                value={it.name}
                // permet le filtrage local par nom + sku
                keywords={[it.name, (it as any).sku ?? '']}
                onSelect={() => {
                  onChange({
                    id: it.id,
                    name: it.name,
                    sku: (it as any).sku ?? null,
                    unitPrice: Number((it as any).unitPrice ?? 0),
                  })
                  setOpen(false)
                  setQuery('')
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value?.id === it.id ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <div className='flex flex-col'>
                  <span className='font-medium'>{it.name}</span>
                  <span className='text-xs text-muted-foreground'>
                    {(it as any).sku ? `SKU: ${(it as any).sku}` : '—'} • PU:{' '}
                    {Number((it as any).unitPrice ?? 0).toLocaleString()}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>

          {/* Bouton "Créer" toujours dispo si on a saisi quelque chose */}
          {query.trim() ? (
            <div className='border-t p-2'>
              <Button variant='ghost' size='sm' onClick={createNew}>
                <Plus className='h-4 w-4 mr-1' /> Créer « {query.trim()} »
              </Button>
            </div>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
